/**
 * ตารางเส้นทาง — **แหล่งเดียว** ที่ทั้ง router และ test อ่าน
 *
 * ★ ทำไมต้องเป็นข้อมูล ไม่ใช่เขียนกระจายในแต่ละหน้า
 *   บน iPad ที่เปิดจากไอคอนหน้าจอโฮม (standalone) **ไม่มีแถบเบราว์เซอร์
 *   และปัดกลับไม่ได้** ทางออกทางเดียวคือปิดแอปทิ้ง ทุกหน้าจึงต้องมีทางออก
 *   ของตัวเอง และต้องมีที่เดียวที่ตอบได้ว่า "หน้านี้กลับไปไหน"
 *
 * ★ **ห้ามใช้ `history.back()`** เด็ดขาด
 *   - เปิดจากไอคอนหน้าจอโฮมแล้วเข้าตรงจาก URL → ไม่มีประวัติให้ย้อน กดแล้วไม่เกิดอะไร
 *   - หรือย้อนออกไปนอกแอปเลย ซึ่งแย่กว่าไม่มีปุ่ม
 *   `parent` ข้างล่างจึงเป็น **เส้นทางที่กำหนดไว้** ไม่ใช่การเดาจากประวัติ
 *
 * ⚠️ เพิ่ม route ใหม่ต้องเพิ่มที่นี่ — `App.tsx` สร้าง `<Route>` จากตารางนี้
 *   และ e2e ไล่ทุกแถวเพื่อยืนยันว่ามีทางออก ถ้าลืมใส่ ทั้งสองอย่างจะไม่รู้จักหน้านั้น
 */

export type PageName =
  | 'gate'
  | 'register'
  | 'subjects'
  | 'topics'
  | 'quiz'
  | 'result'
  | 'parent'

export interface RouteMeta {
  /** pattern ของ react-router */
  path: string
  page: PageName
  /** ต้องเข้าสู่ระบบก่อนถึงจะเข้าได้ */
  guarded: boolean
  /** เด็กเดินไปถึงหน้านี้ได้จริงไหม (ใช้คัดว่าต้องมีทางออกแบบเด็กกดเองได้) */
  learnerReachable: boolean
  /**
   * หน้าแม่ — ปุ่มย้อนกลับพาไปที่นี่
   * `null` = เป็นหน้าแรกของเส้นทางนั้น ไม่มีปุ่มย้อนกลับ
   */
  parent: string | null
  /**
   * ออกจากหน้านี้ต้องถามยืนยันก่อนไหม
   * true เฉพาะหน้าที่การออกกลางคันมีความหมาย (หน้าทำข้อสอบ)
   */
  confirmOnLeave: boolean
  /** ตัวอย่าง path จริงไว้ให้ test เปิด (แทนค่าพารามิเตอร์แล้ว) */
  sample: string
}

export const ROUTES: RouteMeta[] = [
  {
    path: '/enter',
    page: 'gate',
    guarded: false,
    learnerReachable: true,
    // หน้าแรกสุดของทุกคน — "ย้อนกลับ" จากที่นี่ไม่มีความหมาย
    parent: null,
    confirmOnLeave: false,
    sample: '/enter',
  },
  {
    path: '/register',
    page: 'register',
    guarded: false,
    learnerReachable: false,
    parent: '/enter',
    confirmOnLeave: false,
    sample: '/register',
  },
  {
    path: '/learn',
    page: 'subjects',
    guarded: true,
    learnerReachable: true,
    // ★ หน้าแรกของเด็ก — ไม่มีปุ่มย้อนกลับ แต่มี "ออกก่อน" ไว้ออกจากระบบ
    parent: null,
    confirmOnLeave: false,
    sample: '/learn',
  },
  {
    path: '/learn/:code',
    page: 'topics',
    guarded: true,
    learnerReachable: true,
    parent: '/learn',
    confirmOnLeave: false,
    sample: '/learn/math',
  },
  {
    path: '/quiz/:quizId',
    page: 'quiz',
    guarded: true,
    learnerReachable: true,
    parent: '/learn',
    // ★ ออกกลางข้อสอบต้องถามก่อน — แต่ถามเพื่อ **ยืนยันเจตนา** ไม่ใช่เตือนว่างานจะหาย
    //   คำตอบถูกบันทึกแล้วและกลับมาทำต่อจากข้อเดิมได้ (คำตัดสินข้อ 3)
    confirmOnLeave: true,
    sample: '/quiz/q1',
  },
  {
    path: '/result/:attemptId',
    page: 'result',
    guarded: true,
    learnerReachable: true,
    // ★ ทำเสร็จแล้วไม่มีอะไรจะเสีย ย้อนกลับได้ทันที ไม่ต้องถาม
    parent: '/learn',
    confirmOnLeave: false,
    sample: '/result/a1',
  },
  {
    path: '/parent',
    page: 'parent',
    guarded: true,
    learnerReachable: false,
    parent: '/enter',
    confirmOnLeave: false,
    sample: '/parent',
  },
]

/** แปลง pattern เป็น RegExp — `:param` แทนหนึ่งช่วงที่ไม่มี `/` */
function toPattern(path: string): RegExp {
  const body = path
    .split('/')
    .map((seg) => (seg.startsWith(':') ? '[^/]+' : seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))
    .join('/')
  return new RegExp(`^${body}/?$`)
}

export function metaFor(pathname: string): RouteMeta | null {
  return ROUTES.find((r) => toPattern(r.path).test(pathname)) ?? null
}

/** หน้าแม่ของ path นี้ — `null` แปลว่าไม่ต้องมีปุ่มย้อนกลับ */
export function parentOf(pathname: string): string | null {
  return metaFor(pathname)?.parent ?? null
}
